import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  useCallback,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

interface SessionUser {
  email?: string;
  name?: string;
  picture?: string;
}

interface SessionContextType {
  token: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  user: SessionUser | null;
  logout: () => void;
  login: () => void;
  switchAccount: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

/**
 * Thin session wrapper around Auth0. The full role/JWT-decode pattern from
 * admin-web is deferred — for the minimal slice we just need token + user.
 * When Auth0 isn't configured (env vars missing), Auth0ProviderWithNavigate
 * renders children passthrough, and useAuth0 returns the default unauthenticated
 * context — so this works without special-casing.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const auth0 = useAuth0();
  const auth0Configured = Boolean(
    import.meta.env.VITE_AUTH0_DOMAIN && import.meta.env.VITE_AUTH0_CLIENT_ID
  );
  const {
    isLoading: isAuth0Loading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
    user: auth0User,
  } = auth0;

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  const logout = useCallback(() => {
    if (auth0Configured && auth0Logout) {
      void auth0Logout({
        logoutParams: { returnTo: window.location.origin },
      });
    }
  }, [auth0Configured, auth0Logout]);

  useEffect(() => {
    if (auth0Configured && isAuth0Loading) {
      return;
    }
    if (!auth0Configured || !isAuthenticated || !getAccessTokenSilently) {
      setIsLoading(false);
      setToken(null);
      return;
    }
    const getToken = async () => {
      try {
        const t = await getAccessTokenSilently();
        setToken(t);
      } catch (error) {
        console.error("Error getting token:", error);
      } finally {
        setIsLoading(false);
      }
    };
    void getToken();
  }, [
    auth0Configured,
    isAuthenticated,
    getAccessTokenSilently,
    isAuth0Loading,
  ]);

  const user: SessionUser | null = auth0User
    ? {
        email: auth0User.email,
        name: auth0User.name,
        picture: auth0User.picture,
      }
    : null;

  return (
    <SessionContext.Provider
      value={{
        isLoading: (auth0Configured && isAuth0Loading) || isLoading,
        isLoggedIn: auth0Configured && isAuthenticated && !!token,
        user,
        token,
        login: () => {
          if (auth0Configured && loginWithRedirect) void loginWithRedirect();
        },
        switchAccount: () => {
          if (auth0Configured && loginWithRedirect) {
            void loginWithRedirect({
              authorizationParams: { prompt: "login" },
            });
          }
        },
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
